import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  VStack,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
  Box,
} from '@chakra-ui/react';

import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { learningPlanActions } from 'store/modules/learningPlan/learningPlan-slice';
import FormikFormControl from 'components/common/forms/FormikFormControl';
import MultiEmailInput from 'components/common/forms/MultiEmailInput';
import { ReducerStatus } from 'utils/constants';
import { useState } from 'react';
import { createLearningPlanAction } from 'store/modules/learningPlan/learningPlan-action';
import ResultModal from 'components/common/modals/ResultModal';

function CreateLearningPlanMd() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State for managing selected users/groups in MultiEmailInput
  const [selectedEmails, setSelectedEmails] = useState([]);

  /**
   * Handles adding a new item (user or group) to the selected emails list
   */
  const handleEmailAdd = (item) => {
    setSelectedEmails((prev) => [...prev, item]);
  };

  /**
   * Handles removing an item from the selected emails list
   */
  const handleEmailRemove = (item) => {
    setSelectedEmails((prev) =>
      prev.filter(
        (email) => email.value !== item.value || email.type !== item.type
      )
    );
  };

  const createLearningPlan = useSelector(
    (state) => state.learningPlan.createLearningPlan
  );
  const learningPlanId = useSelector(
    (state) => state.learningPlan.learningPlan?.id
  );

  const [formInputs, setFormInputs] = useState({
    name: '',
    description: '',
  });

  const formValidation = Yup.object({
    name: Yup.string().max(155).required('Required'),
    description: Yup.string().max(1000).required('Required'),
  });

  const handleSubmitForm = (formikObject) => {
    // Convert selectedEmails to simple email strings for backend compatibility
    const emails = selectedEmails.map((item) => item.value);

    const learningPlanData = {
      ...formikObject,
      OwnerGroups: emails,
    };

    dispatch(createLearningPlanAction(learningPlanData));
  };

  const handleResultButton = () => {
    dispatch(
      learningPlanActions.setCreateLearningPlan({
        status: ReducerStatus.IDLE,
      })
    );
    if (createLearningPlan.status === ReducerStatus.SUCCEEDED) {
      setFormInputs({
        name: '',
        description: '',
      });
      setSelectedEmails([]);

      dispatch(
        learningPlanActions.setCreateLearningPlan({
          modal: false,
          status: ReducerStatus.IDLE,
          error: null,
        })
      );
      navigate(`${learningPlanId}`);
    }
  };

  const handleToggleCreateModal = (isOpen) => {
    dispatch(
      learningPlanActions.setCreateLearningPlan({
        modal: isOpen,
      })
    );
  };

  return (
    <Box>
      {createLearningPlan.status === ReducerStatus.SUCCEEDED ||
      createLearningPlan.status === ReducerStatus.FAILED ? (
        <ResultModal
          isOpen={createLearningPlan.modal}
          onSubmit={handleResultButton}
          isError={createLearningPlan.status === ReducerStatus.FAILED}
          title={
            createLearningPlan.status === ReducerStatus.SUCCEEDED
              ? 'Learning Plan Created'
              : null
          }
          message={
            createLearningPlan.status === ReducerStatus.SUCCEEDED
              ? 'The learning plan has been created successfully.'
              : createLearningPlan.error?.data?.title || 'Server Error'
          }
          errorObject={createLearningPlan.error?.data?.errors}
        />
      ) : (
        <Modal
          size="2xl"
          isOpen={createLearningPlan.modal}
          onClose={() => handleToggleCreateModal(false)}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Create Learning Plan</ModalHeader>
            <ModalCloseButton />

            <Formik
              enableReinitialize={false}
              initialValues={formInputs}
              onSubmit={(values) => {
                handleSubmitForm(values);
              }}
              validationSchema={formValidation}
            >
              {() => (
                <Form>
                  <ModalBody my={3}>
                    <VStack spacing={6} align="stretch">
                      <Field name="name">
                        {({ field, form }) => (
                          <FormikFormControl
                            label="Name"
                            helperText="Enter the name of the learning plan. Make it concise and descriptive."
                            errors={form.errors.name}
                            form={form}
                            touched={form.touched.name}
                          >
                            <Input {...field} />
                          </FormikFormControl>
                        )}
                      </Field>

                      <Field name="description">
                        {({ field, form }) => (
                          <FormikFormControl
                            label="Description"
                            helperText="Provide a detailed description of the learning plan. Mention the key objectives and what learners can expect to gain."
                            errors={form.errors.description}
                            touched={form.touched.description}
                          >
                            <Textarea {...field} />
                          </FormikFormControl>
                        )}
                      </Field>

                      <MultiEmailInput
                        label="Owner Groups"
                        helperText="Search and select users or distribution groups that have the authority to modify and use this learning plan. You can switch between searching users and groups using the dropdown."
                        selectedItems={selectedEmails}
                        isError={false}
                        errorMessage=""
                        onItemAdd={handleEmailAdd}
                        onItemRemove={handleEmailRemove}
                        defaultSearchType="groups"
                      />
                    </VStack>
                  </ModalBody>

                  <ModalFooter>
                    <Button
                      variant="outline"
                      colorScheme="blue"
                      mr={3}
                      onClick={() => handleToggleCreateModal(false)}
                    >
                      Close
                    </Button>
                    <Button
                      colorScheme="red"
                      px={10}
                      type="submit"
                      isLoading={
                        createLearningPlan.status === ReducerStatus.LOADING
                      }
                    >
                      Submit
                    </Button>
                  </ModalFooter>
                </Form>
              )}
            </Formik>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}

export default CreateLearningPlanMd;
